import { Link } from "react-router-dom";

import { FaAmazon } from "react-icons/fa";
import { Button } from "components/core/basic/Button";
import { TextField } from "components/fields/base/TextField";
import { Flex } from "../../styled-system/jsx";
import { Label } from "react-aria-components";
import { inputClassNames } from "components/fields/styles/styles";
import { getAPIKeys } from "utils/getUrlPrefix";
import { apiClient } from "zodios/client";
import clearCookies from "utils/clearCookies";
import setAuthToken from "utils/setAuthToken";
import { setCurrentUser } from "redux/reducers/userReducer";
import { useAppDispatch } from "redux/hooks";
import userMarketPlace from "utils/userMarketPlace";
import { setTokens } from "redux/reducers/authReducers";
import { setChannel } from "redux/reducers/channel";
import { refreshAmzOnboardStatus, updateAmzOnboardingStatus } from "redux/reducers/amzOnboardReducer";
import { setErrors } from "redux/reducers/errorReducers";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { css, cx } from "../../styled-system/css";
import { LWAButton, type LWAResult } from "../components/custom/LoginWithAmazon";
import { AuthLayout } from "layouts/AuthLayout";
import { captureException } from "@sentry/react";

const apiKeys = getAPIKeys();

const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

declare global {
  interface Window {
    _mfq?: Array<[command: string, ...args: unknown[]]>;
  }
}

interface LoginFormData {
  email: string;
  password: string;
}

interface Props {
  onSignUpClick: () => void;
}

const INITIAL_DATA: LoginFormData = {
  email: "",
  password: "",
};

const Login = ({ onSignUpClick }: Props) => {
  const dispatch = useAppDispatch();
  const classes = inputClassNames();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: INITIAL_DATA,
  });

  const socialLoginMutation = useMutation({
    mutationFn: async (user: LWAResult) => {
      const userLoginInfo = {
        grant_type: "convert_token" as const,
        backend: "amazon",
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        token: user.accessToken,
      };

      const authRes = await apiClient.fetchLoginUserVerification({
        custom_api_request: "user_login_verification",
        user_email: user.email,
      });

      if (authRes === "User Available") {
        const tokenRes = await apiClient.convertTokens(userLoginInfo);

        const userProfileRes = await apiClient.fetchLoginUserInfo({
          custom_api_request: "login_user_info",
          user_email: user.email,
        });

        const params = new URLSearchParams(window.location.search);
        const redirect = params.get("redirect");

        setAuthToken(tokenRes.access_token);
        const tokens = {
          access_token: tokenRes.access_token,
          refresh_token: tokenRes.refresh_token,
        };

        localStorage.setItem("Tokens", JSON.stringify(tokens));

        const mfq = window._mfq || [];

        if (Array.isArray(window._mfq)) {
          mfq.push(["setVariable", "USER", userProfileRes.full_user_name]);
        }

        dispatch(setCurrentUser(userProfileRes));
        dispatch(setTokens(tokens));
        dispatch(setChannel(userProfileRes.channel.toLowerCase()));
        localStorage.setItem("channel", userProfileRes.channel.toLowerCase());

        if (redirect) {
          localStorage.setItem("redirect", redirect);
        }

        userMarketPlace(tokenRes.access_token, () => {
          if (userProfileRes.permissions === "reports") {
            window.location.href = "/user/report/advertisement";
          } else {
            window.location.href = "/user/dashboard";
          }
        });
      } else {
        sessionStorage.clear();
        localStorage.clear();
        clearCookies();
        if (authRes === "User Not Available") {
          setError("root", {
            type: "server",
            message:
              "An error occurred attempting to sign in with Amazon. Did you mean to sign in with an email and password instead?",
          });
        }
      }
    },
  });

  const loginFormMutation = useMutation({
    mutationFn: async (values: LoginFormData) => {
      const { email, password } = values;

      const passwordLoginInfo = {
        grant_type: "password" as const,
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        username: email.trim(),
        password: password,
      };

      const tokensRes = await apiClient.login(passwordLoginInfo);
      const { access_token, refresh_token } = tokensRes;

      refreshAmzOnboardStatus(updateAmzOnboardingStatus, access_token, setErrors);

      const authRes = await apiClient.fetchLoginUserInfo({
        custom_api_request: "login_user_info",
        user_email: email.trim(),
      });

      const tokens = {
        access_token,
        refresh_token,
      };

      if (typeof authRes === "object") {
        const channel = authRes.channel.toLowerCase();

        localStorage.setItem("Tokens", JSON.stringify(tokens));
        localStorage.setItem("channel", channel);

        setAuthToken(access_token);
        setCurrentUser(authRes);
        setChannel(channel);

        // Redirect to dashboard or ads report based on permissions
        userMarketPlace(access_token, () => {
          const params = new URLSearchParams(window.location.search);
          const redirect = params.get("redirect");
          if (redirect) {
            window.location.href = redirect;
            return;
          }
          if (authRes.permissions === "reports") {
            window.location.href = "/user/report/advertisement";
          } else {
            window.location.href = "/user/dashboard";
          }
        });
      }
    },
    onError: (error: { response: { data: { error_description: string } } }) => {
      if (error.response.data.error_description) {
        setError("root", {
          type: "server",
          message: error.response.data.error_description,
        });
      }
    },
  });

  return (
    <AuthLayout title={"Welcome Back!"} maxWidth={"360px"}>
      <>
        <form onSubmit={handleSubmit((data) => loginFormMutation.mutate(data))}>
          <Flex gap={"medium"} direction={"column"}>
            <Controller
              name={"email"}
              control={control}
              rules={{
                required: { value: true, message: "An email is required" },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  label={"Email"}
                  type={"email"}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={"password"}
              control={control}
              rules={{ required: { value: true, message: "A password is required" } }}
              render={({ field, fieldState }) => (
                <TextField
                  label={
                    <Flex justifyContent={"space-between"} width={"100%"}>
                      <Label>Password</Label> <Link to="/signin/resetpassword">Forgot?</Link>
                    </Flex>
                  }
                  type={"password"}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            {typeof errors.root?.message === "string" && (
              <p
                className={cx(
                  classes.error,
                  css({
                    marginX: "auto",
                    textAlign: "center !important",
                  }),
                )}
              >
                {errors.root.message}
              </p>
            )}
          </Flex>
          <Button
            isPending={loginFormMutation.isPending || socialLoginMutation.isPending}
            className={css({ width: "100%", marginTop: "large" })}
            type="submit"
          >
            Log In
          </Button>
        </form>
        <hr className={css({ width: "100%", marginY: "medium" })} />
        <LWAButton
          isPending={loginFormMutation.isPending || socialLoginMutation.isPending}
          onSuccess={(user) => {
            socialLoginMutation.mutate(user);
          }}
          onError={(error) => {
            captureException(error);
          }}
        >
          Continue with Amazon <FaAmazon />
        </LWAButton>
        <div className={css({ width: "100%", marginTop: "medium", textAlign: "center" })}>
          Want to get started today?
          <br />
          <Link
            to="/signup"
            onClick={() => {
              onSignUpClick();
            }}
          >
            Sign Up
          </Link>
        </div>
      </>
    </AuthLayout>
  );
};

export default Login;
